import React from 'react';
import { Checkbox, Tooltip, useMediaQuery } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Logout from '@src/assets/icons/logout.svg';
import QuestionMark from '@src/assets/icons/QuestionMark.svg';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import theme from '@src/styles/theme';
import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
import { BREAKPOINTS } from '@src/styles/config';
import TabletAndroidRoundedIcon from '@mui/icons-material/TabletAndroidRounded';
import { StyledOSName } from './index.styled';

const DeviceItem = ({
  deviceItem,
  fingerprint,
  onClickLogout,
  isPopup,
  className,
  selected,
  onSelect,
  hasSelectDevice = false,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.SMD));

  const isCurrentDevice = fingerprint === deviceItem?.fingerprint;
  const shouldShowLogoutIcon =
    (isPopup || !isCurrentDevice) && !hasSelectDevice;

  const renderLastOnlineAt = (timeLastOnline, locale = 'vn') => {
    if (!timeLastOnline) return '';
    moment.locale(locale);

    const now = moment();
    const timeDiffInHours = now.diff(timeLastOnline, 'hours', true);

    if (timeDiffInHours > 24) {
      return moment(timeLastOnline).format('DD/MM/YYYY');
    }

    return moment(timeLastOnline).fromNow(); // It will automatically adapt to the locale
  };

  const renderDeviceInfo = (device) => {
    const { city, regionName, region, country, countryName } = device || {};
    const deviceCity = city || regionName || region || '';
    const deviceCountry = countryName || country || '';

    return (
      <div className="info-device-wrapper">
        <div className="os-info">
          <span className="os-name">{`${device?.os?.name}${
            device?.client?.name ? ` - ${device?.client?.name}` : ''
          }`}</span>
          {device?.fingerprint === fingerprint && (
            <StyledOSName>
              <div className="blue-dot" />
              <div>{t('thisDevice')}</div>
            </StyledOSName>
          )}
        </div>
        <div className="des-device">
          <div className="des-device-detail">
            <span>
              {deviceCity}
              {deviceCity && deviceCountry && ', '}
              {deviceCountry}
            </span>
            <div className="icon-rounded">•</div>
            <span>{device?.ip}</span>
          </div>
          {!isCurrentDevice && !isMobile && (
            <div className="icon-rounded responsive">•</div>
          )}
          {!isCurrentDevice && (
            <span>
              {t('lastOnlineAtV2')}{' '}
              {renderLastOnlineAt(
                device?.lastOnlineAt || device?.createdAt,
                language,
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  const getCurrentDevice = (device) => {
    const DEVICE = {
      DESKTOP: 'desktop',
      SMARTPHONE: 'smartphone',
      TABLET: 'tablet',
      TELEVISION: 'television',
      APP: 'app',
    };

    const ICONS_DEVICE = {
      [DEVICE.DESKTOP]: <DesktopWindowsRoundedIcon />,
      [DEVICE.SMARTPHONE]: <PhoneAndroidRoundedIcon />,
      [DEVICE.TABLET]: <TabletAndroidRoundedIcon />,
      [DEVICE.TELEVISION]: <TvRoundedIcon />,
      [DEVICE.APP]: <img src={QuestionMark} alt="QuestionMark" />,
    };

    return ICONS_DEVICE[device] || ICONS_DEVICE[DEVICE.APP];
  };

  return (
    <div className={`${className} item-device-info`}>
      {!hasSelectDevice && (
        <div className="icon-device">
          {getCurrentDevice(deviceItem?.device?.type)}
        </div>
      )}
      {hasSelectDevice && (
        <Checkbox
          className="device-checkbox"
          checked={selected}
          onChange={onSelect}
          size="medium"
          inputProps={{ 'aria-label': t('selectDevice') }}
        />
      )}
      <div className="item-name-device">{renderDeviceInfo(deviceItem)}</div>
      {shouldShowLogoutIcon && (
        <Tooltip
          arrow
          title={<span className="tooltip-text">{t('logout')}</span>}
          placement="left"
        >
          <div
            tabIndex="0"
            onClick={onClickLogout}
            role="button"
            className="btn-logout"
          >
            <img src={Logout} alt="logout" />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default DeviceItem;
